import React, { useState } from 'react';
import { Users } from 'lucide-react';
import { useNavigate } from 'react-router-dom';
import { supabase } from './lib/supabase';

function Auth() {
  const navigate = useNavigate();
  const [authMode, setAuthMode] = useState<
    'default' | 'signIn' | 'signUp' | 'managementSignIn' | 'managementSignUp'
  >('default');
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [organization, setOrganization] = useState('');

  const handleCancel = () => {
    setAuthMode('default');
    setEmail('');
    setPassword('');
    setOrganization('');
  };

  // Regular sign in
  const handleSignInSubmit = async (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    const { data, error } = await supabase.auth.signInWithPassword({ email, password });
    if (error) {
      alert("Sign In Error: " + error.message);
    } else {
      navigate('/app');
    }
  };

  // Regular sign up
  const handleSignUpSubmit = async (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    try {
      const { data, error: signUpError } = await supabase.auth.signUp({ 
        email, 
        password,
        options: {
          data: {
            is_management: false
          }
        }
      });

      if (signUpError) throw signUpError;
      if (!data?.user?.id) throw new Error('User creation failed - no user ID returned');

      const { error: orgError } = await supabase
        .from('user_organization')
        .insert({
          user_id: data.user.id,
          organization: 'default-org'
        });

      if (orgError) throw orgError;
      navigate('/app');
    } catch (error) {
      alert("Sign Up Error: " + (error as Error).message);
    }
  };

  // Management sign in
  const handleManagementSignInSubmit = async (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    try {
      const { data: signInData, error: signInError } = await supabase.auth.signInWithPassword({ 
        email, 
        password 
      });

      if (signInError) throw signInError;

      const { data: managementData, error: managementError } = await supabase
        .from('management_users')
        .select('user_id')
        .eq('user_id', signInData.user.id);

      if (managementError) throw managementError;

      if (!managementData || managementData.length === 0) {
        await supabase.auth.signOut();
        alert("Access denied: Management access required");
        return;
      }

      navigate('/app');
    } catch (error) {
      await supabase.auth.signOut();
      alert("Management Sign In Error: " + (error as Error).message);
    }
  };

  // Add management sign up handler
  const handleManagementSignUpSubmit = async (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    try {
      const { data, error: signUpError } = await supabase.auth.signUp({ 
        email, 
        password,
        options: {
          data: {
            is_management: true
          }
        }
      });

      if (signUpError) throw signUpError;
      if (!data?.user?.id) throw new Error('User creation failed - no user ID returned');

      // Insert into management_users table
      const { error: managementError } = await supabase
        .from('management_users')
        .insert({
          user_id: data.user.id,
          organization: organization
        });

      if (managementError) throw managementError;
      
      alert('Management account created! Please check your email to confirm your account.');
      navigate('/auth');
    } catch (error) {
      alert("Management Sign Up Error: " + (error as Error).message);
    }
  };

  return (
    <div className="min-h-screen bg-gray-50 flex items-center justify-center relative">
      <button
        onClick={() => navigate('/')}
        className="absolute top-4 left-4 px-4 py-2 bg-blue-500 text-white rounded-lg font-semibold hover:bg-blue-400 transition-colors flex items-center"
      >
        ← Return to Home Page
      </button>

      <div className="bg-white p-8 rounded-md shadow-md max-w-md w-full">
        <div className="flex justify-center mb-8">
          <div className="text-center">
            <div className="flex items-center justify-center">
              <Users className="h-8 w-8 text-indigo-600" />
              <h1 className="text-2xl font-bold text-indigo-600 ml-2">EZ-CRM</h1>
            </div>
            <h2 className="text-sm text-indigo-600 mt-1">Efficient, Effective, EZ.</h2>
          </div>
        </div>

        {authMode === 'default' && (
          <div className="space-y-4">
            <button
              onClick={() => setAuthMode('signIn')}
              className="w-full py-2 px-4 bg-indigo-600 text-white rounded-md hover:bg-indigo-700 focus:outline-none focus:ring-2 focus:ring-indigo-500"
            >
              Sign In
            </button>
            <button
              onClick={() => setAuthMode('signUp')}
              className="w-full py-2 px-4 border border-indigo-600 text-indigo-600 rounded-md hover:bg-indigo-50 focus:outline-none focus:ring-2 focus:ring-indigo-500"
            >
              Sign Up
            </button>
            <button
              onClick={() => setAuthMode('managementSignIn')}
              className="w-full py-2 px-4 bg-green-600 text-white rounded-md hover:bg-green-700 focus:outline-none focus:ring-2 focus:ring-green-500"
            >
              Management Sign In
            </button>
            <button
              onClick={() => setAuthMode('managementSignUp')}
              className="w-full py-2 px-4 border border-green-600 text-green-600 rounded-md hover:bg-green-50 focus:outline-none focus:ring-2 focus:ring-green-500"
            >
              Management Sign Up
            </button>
            <button
              className="w-full py-2 px-4 bg-blue-500 text-white rounded-md opacity-75 cursor-not-allowed"
            >
              Enterprise Management (Coming Soon)
            </button>
          </div>
        )}

        {authMode === 'signIn' && (
          <form onSubmit={handleSignInSubmit} className="space-y-4">
            <div>
              <label className="block text-sm font-medium text-gray-700">Email</label>
              <input
                type="email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-indigo-500 focus:ring-indigo-500"
                required
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700">Password</label>
              <input
                type="password"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-indigo-500 focus:ring-indigo-500"
                required
              />
            </div>
            <div className="flex justify-end space-x-2">
              <button
                type="button"
                onClick={handleCancel}
                className="px-4 py-2 text-sm text-gray-700 hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-indigo-500"
              >
                Cancel
              </button>
              <button
                type="submit"
                className="px-4 py-2 text-sm text-white bg-indigo-600 rounded-md hover:bg-indigo-700 focus:outline-none focus:ring-2 focus:ring-indigo-500"
              >
                Sign In
              </button>
            </div>
          </form>
        )}

        {authMode === 'signUp' && (
          <form onSubmit={handleSignUpSubmit} className="space-y-4">
            <div>
              <label className="block text-sm font-medium text-gray-700">Email</label>
              <input
                type="email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-indigo-500 focus:ring-indigo-500"
                required
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700">Password</label>
              <input
                type="password"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-indigo-500 focus:ring-indigo-500"
                required
              />
            </div>
            <div className="flex justify-end space-x-2">
              <button
                type="button"
                onClick={handleCancel}
                className="px-4 py-2 text-sm text-gray-700 hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-indigo-500"
              >
                Cancel
              </button>
              <button
                type="submit"
                className="px-4 py-2 text-sm text-white bg-indigo-600 rounded-md hover:bg-indigo-700 focus:outline-none focus:ring-2 focus:ring-indigo-500"
              >
                Sign Up
              </button>
            </div>
          </form>
        )}

        {authMode === 'managementSignIn' && (
          <form onSubmit={handleManagementSignInSubmit} className="space-y-4">
            <div>
              <label className="block text-sm font-medium text-gray-700">Email</label>
              <input
                type="email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-indigo-500 focus:ring-indigo-500"
                required
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700">Password</label>
              <input
                type="password"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-indigo-500 focus:ring-indigo-500"
                required
              />
            </div>
            <div className="flex justify-end space-x-2">
              <button
                type="button"
                onClick={handleCancel}
                className="px-4 py-2 text-sm text-gray-700 hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-green-500"
              >
                Cancel
              </button>
              <button
                type="submit"
                className="px-4 py-2 text-sm text-white bg-green-600 rounded-md hover:bg-green-700 focus:outline-none focus:ring-2 focus:ring-green-500"
              >
                Sign In as Management
              </button>
            </div>
          </form>
        )}

        {authMode === 'managementSignUp' && (
          <form onSubmit={handleManagementSignUpSubmit} className="space-y-4">
            <div>
              <label className="block text-sm font-medium text-gray-700">Email</label>
              <input
                type="email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-indigo-500 focus:ring-indigo-500"
                required
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700">Password</label>
              <input
                type="password"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-indigo-500 focus:ring-indigo-500"
                required
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700">Organization Name</label>
              <input
                type="text"
                value={organization}
                onChange={(e) => setOrganization(e.target.value)}
                className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-indigo-500 focus:ring-indigo-500"
                required
              />
            </div>
            <div className="flex justify-end space-x-2">
              <button
                type="button"
                onClick={handleCancel}
                className="px-4 py-2 text-sm text-gray-700 hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-green-500"
              >
                Cancel
              </button>
              <button
                type="submit"
                className="px-4 py-2 text-sm text-white bg-green-600 rounded-md hover:bg-green-700 focus:outline-none focus:ring-2 focus:ring-green-500"
              >
                Sign Up as Management
              </button>
            </div>
          </form>
        )}
      </div>
    </div>
  );
}

export default Auth;
